import { useAccount } from '@shared/api';
import { PackagingActivityRecord } from '@shared/api/types';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { TextCell } from '@shared/components/data-table/cells/text-cell';
import Unit from '@shared/components/content/unit';
import { formatCost, getCurrencySymbol } from '@shared/lib/format';
import { ColumnDef } from '@tanstack/react-table';

export function usePurchasedPackagingItemPurchasesTableColumns(): ColumnDef<PackagingActivityRecord>[] {
  const { data: account } = useAccount(true);

  return [
    {
      id: 'uuid',
      accessorKey: 'uuid',
      enableHiding: true,
    },
    {
      id: 'period',
      accessorKey: 'timePeriod',
      header: ({ column }) => <HeaderCell column={column} name="Period" />,
      cell: ({ getValue }) => {
        const period = getValue<PackagingActivityRecord['timePeriod']>();
        return <TextCell text={period?.name} />;
      },
      meta: {
        label: 'Time period',
        sorting: { ascLabel: 'Oldest', descLabel: 'Newest' },
      },
      enableColumnFilter: false,
    },
    {
      id: 'site',
      accessorKey: 'site',
      enableColumnFilter: false,
      header: ({ column }) => <HeaderCell column={column} name="Site" />,
      cell: ({ getValue }) => {
        const site = getValue<PackagingActivityRecord['site']>();
        return <TextCell text={site?.name} />;
      },
      meta: {
        label: 'Site',
        sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' },
      },
    },
    {
      id: 'supplier',
      accessorKey: 'supplier',
      enableSorting: false,
      header: ({ column }) => <HeaderCell column={column} name="Supplier" />,
      cell: ({ getValue }) => {
        const supplier = getValue<PackagingActivityRecord['supplier']>();
        return <TextCell text={supplier?.name} />;
      },
    },

    {
      id: 'quantity',
      accessorKey: 'quantity',
      header: ({ column }) => (
        <HeaderCell column={column} name="Packs purchased" numberColumn />
      ),
      cell: ({ getValue }) => {
        const quantity = getValue<number>();
        return <NumberCell number={quantity} />;
      },
    },

    {
      id: 'totalQuantity',
      accessorKey: 'totalQuantity',
      header: ({ column }) => (
        <HeaderCell column={column} name="Total quantity" numberColumn />
      ),
      cell: ({ getValue }) => {
        const totalQuantity = getValue<number>();
        return <NumberCell number={totalQuantity} />;
      },
    },
    {
      id: 'totalSpend',
      accessorKey: 'totalSpend',
      header: ({ column }) => (
        <HeaderCell column={column} name="Total spend" numberColumn />
      ),
      cell: ({ getValue }) => {
        const totalSpend = getValue<number | null | undefined>();
        return (
          <TextCell
            text={formatCost(totalSpend, account?.defaultCurrency)}
            type="number"
          />
        );
      },
    },
    {
      // Actual price paid per unit for this purchase (backend `price_per_unit`,
      // spend ÷ quantity), matching the list "Average price". accessorFn (with
      // an explicit id) makes the column sortable while keeping the `price` id
      // that maps to the backend sort.
      id: 'price',
      accessorFn: (row) => row.pricePerUnit,
      enableSorting: true,
      header: ({ column }) => (
        <HeaderCell
          column={column}
          name="Price"
          numberColumn
          unit={
            <Unit
              variant={`${getCurrencySymbol(account?.defaultCurrency)}/unit`}
            />
          }
        />
      ),
      cell: ({ row }) => (
        <TextCell
          text={formatCost(row.original.pricePerUnit, account?.defaultCurrency)}
          type="number"
        />
      ),
    },
  ];
}
